import React, { useState, useEffect, useLayoutEffect } from 'react';
import { marked } from 'marked';
import { v4 as uuidv4 } from 'uuid';
import FingerprintJS from '@fingerprintjs/fingerprintjs';
import posthog from 'posthog-js';
import WelcomePage from './components/welcomePage';
import GuestNameInterface from './components/GuestNameInterface';
import ChatInterface from './components/ChatInterface';

marked.setOptions({ breaks: true, gfm: true });
marked.use({
  renderer: {
    link({ href, text }) {
      return `<a href="${href}" target="_blank" rel="noopener noreferrer">${text}</a>`;
    }
  }
});

const POSTHOG_KEY = process.env.REACT_APP_PUBLIC_POSTHOG_PROJECT_TOKEN;
const POSTHOG_HOST = process.env.REACT_APP_PUBLIC_POSTHOG_HOST || 'https://us.i.posthog.com';

if (POSTHOG_KEY && !posthog.__loaded) {
  posthog.init(POSTHOG_KEY, {
    api_host: POSTHOG_HOST,
    defaults: '2026-05-30'
  });
}

export default function Chatbot() {
  const [propertyId, setPropertyId] = useState(() => localStorage.getItem('propertyId') || '');
  const [isLoggedIn, setIsLoggedIn] = useState(false);
  const [messages, setMessages] = useState([]);
  const [loading, setLoading] = useState(false);
  const sessionKey = propertyId ? `${propertyId}SessionId` : '';
  const sessionId = sessionKey ? (localStorage.getItem(sessionKey) || '') : '';
  const [displayedHotelName, setDisplayedHotelName] = useState('');
  const [hotelLogoPath, setHotelLogoPath] = useState('');
  const [suggestions, setSuggestions] = useState([]);
  const [hasSeenWelcome, setHasSeenWelcome] = useState(false);
  const [showNameInterface, setShowNameInterface] = useState(false);
  const [hotelValidated, setHotelValidated] = useState(false);
  const [hotelValidationError, setHotelValidationError] = useState('');
  const [isValidatingHotel, setIsValidatingHotel] = useState(true);
  const [hasMoreHistory, setHasMoreHistory] = useState(false);
  const [oldestCursor, setOldestCursor] = useState(null);
  const [isLoadingHistory, setIsLoadingHistory] = useState(false);

  // Use useLayoutEffect for immediate DOM updates related to layout
  useLayoutEffect(() => {
    const setAppHeight = () => {
      const vh = window.innerHeight * 0.01;
      document.documentElement.style.setProperty('--app-height', `${vh * 100}px`);
    };

    // Set height on initial load and resize events
    setAppHeight();
    window.addEventListener('resize', setAppHeight);

    return () => window.removeEventListener('resize', setAppHeight);
  }, []);

  const capturePostHogEvent = (eventName, properties = {}) => {
    if (!POSTHOG_KEY || typeof posthog.capture !== 'function') return;

    const currentPropertyId = propertyId || localStorage.getItem('propertyId') || undefined;
    const currentSessionId = currentPropertyId
      ? localStorage.getItem(`${currentPropertyId}SessionId`) || sessionId
      : sessionId;

    posthog.capture(eventName, {
      property_id: currentPropertyId,
      hotel_name: displayedHotelName || undefined,
      has_session: Boolean(currentSessionId),
      has_guest_profile: Boolean(guestName && guestLastName),
      ...properties
    });
  };

  const identifyPostHogGuest = (distinctId, properties = {}) => {
    if (!POSTHOG_KEY || !distinctId || typeof posthog.identify !== 'function') return;

    posthog.identify(distinctId, {
      property_id: propertyId || localStorage.getItem('propertyId') || undefined,
      hotel_name: displayedHotelName || undefined,
      ...properties
    });
  };

  // Validate hotel and fetch hotel info
  const validateHotel = async (propertyIdToValidate) => {
    setIsValidatingHotel(true);
    setHotelValidationError('');

    try {
      console.log('Validating hotel:', propertyIdToValidate);
      const res = await fetch(`${process.env.REACT_APP_API_BASE}/auth/validate-hotel?hotel=${propertyIdToValidate}`);

      console.log('Response status:', res.status);
      console.log('Response ok:', res.ok);

      // Check if response is ok (status in range 200-299)
      if (!res.ok) {
        console.error('Server returned error status:', res.status);
        capturePostHogEvent('hotel_validation_failed', {
          property_id: propertyIdToValidate,
          reason: 'server_error',
          status: res.status
        });
        setHotelValidationError(`Server error: ${res.status}. Please try again.`);
        setIsValidatingHotel(false);
        return;
      }

      const data = await res.json();
      console.log('API Response:', data);

      if (data.hotelExists) {
        console.log('Hotel exists! Setting up hotel data...');
        setHotelValidated(true);

        if (data.hotelName) {
          setDisplayedHotelName(data.hotelName);
        }

        if (data.hotelLogoUrl) {
          const logoPath = data.hotelLogoUrl.startsWith('http')
            ? data.hotelLogoUrl
            : `https://api.kypser.com${data.hotelLogoUrl}`;
          setHotelLogoPath(logoPath);
        }

        if (data.suggestions) {
          // Suggestions is already an array, no need to parse
          setSuggestions(data.suggestions);
        }

        const welcome = `Hello, hola, γεια! Welcome to ${data.hotelName || 'our'}! How can I assist you today?`;
        setMessages([{ role: 'bot', content: welcome, html: marked(welcome), msgId: 'welcome-msg', isHistory: true }]);
        capturePostHogEvent('hotel_validation_succeeded', {
          property_id: propertyIdToValidate,
          hotel_name: data.hotelName || undefined,
          has_logo: Boolean(data.hotelLogoUrl),
          suggestions_count: Array.isArray(data.suggestions) ? data.suggestions.length : 0
        });

        setIsValidatingHotel(false);
      } else {
        console.error('Hotel validation failed: hotel does not exist', data);
        capturePostHogEvent('hotel_validation_failed', {
          property_id: propertyIdToValidate,
          reason: 'hotel_not_found',
          error: data.error || undefined
        });
        setHotelValidationError(data.error || 'Hotel not found. Please check your link.');
        setIsValidatingHotel(false);
      }
    } catch (e) {
      console.error('Validation error (catch block):', e);
      console.error('Error details:', e.message, e.stack);
      capturePostHogEvent('hotel_validation_failed', {
        property_id: propertyIdToValidate,
        reason: 'network_error'
      });
      setHotelValidationError('Failed to connect to server. Please try again.');
      setIsValidatingHotel(false);
    }
  };

  // Extract propertyId from URL and validate on mount
  useEffect(() => {
    const extractAndValidateHotel = async () => {
      try {
        // Extract propertyId from URL query parameter
        const urlParams = new URLSearchParams(window.location.search);
        const propertyIdFromUrl = urlParams.get('propertyId');

        console.log('URL propertyId:', propertyIdFromUrl);

        if (propertyIdFromUrl) {
          // Check if different from current propertyId in localStorage
          const storedPropertyId = localStorage.getItem('propertyId');

          if (storedPropertyId && storedPropertyId !== propertyIdFromUrl) {
            console.log('Different propertyId detected, replacing:', storedPropertyId, '->', propertyIdFromUrl);
            capturePostHogEvent('hotel_context_changed', {
              previous_property_id: storedPropertyId,
              property_id: propertyIdFromUrl
            });
            // Clear old hotel data
            localStorage.removeItem(`${storedPropertyId}SessionId`);
            localStorage.removeItem(`${storedPropertyId}GuestName`);
            localStorage.removeItem(`${storedPropertyId}GuestLastName`);
            localStorage.removeItem('authToken');
            localStorage.removeItem('hotelLatitude');
            localStorage.removeItem('hotelLongitude');
            setIsLoggedIn(false);
            setHasSeenWelcome(false);
            setGuestName('');
            setGuestLastName('');
            setShowNameInterface(true);
          }

          // Store new propertyId
          localStorage.setItem('propertyId', propertyIdFromUrl);
          setPropertyId(propertyIdFromUrl);

          // Track WhatsApp link open (fire-and-forget)
          const trackingId = urlParams.get('t');
          if (trackingId) {
            fetch(`${process.env.REACT_APP_API_BASE}/tracking/whatsapp-open`, {
              method: 'POST',
              headers: { 'Content-Type': 'application/json' },
              body: JSON.stringify({ trackingId }),
            }).catch(() => { }); // silently ignore errors
            capturePostHogEvent('whatsapp_link_opened', {
              property_id: propertyIdFromUrl,
              has_tracking_id: true
            });
          }

          // Track QR code scan — only once per browser tab session (sessionStorage
          // is cleared when the tab closes, so a genuine new scan always registers)
          const qrGuardKey = `qr_tracked_${propertyIdFromUrl}`;
          if (urlParams.get('qr') === '1' && !sessionStorage.getItem(qrGuardKey)) {
            sessionStorage.setItem(qrGuardKey, '1');

            // Ensure we have a sessionId (fingerprint) before firing the tracking call
            let existingSessionId = localStorage.getItem(`${propertyIdFromUrl}SessionId`);
            
            if (!existingSessionId) {
              try {
                const fp = await FingerprintJS.load();
                const result = await fp.get();
                existingSessionId = result.visitorId;
                // Store it so subsequent calls (like handleNameSubmit) can reuse it
                localStorage.setItem(`${propertyIdFromUrl}SessionId`, existingSessionId);
              } catch (fpErr) {
                console.error('Error generating fingerprint for QR tracking:', fpErr);
                // Fallback to UUID if FingerprintJS fails
                existingSessionId = uuidv4();
                localStorage.setItem(`${propertyIdFromUrl}SessionId`, existingSessionId);
              }
            }

            fetch(`${process.env.REACT_APP_API_BASE}/tracking/qr-scan`, {
              method: 'POST',
              headers: { 'Content-Type': 'application/json' },
              body: JSON.stringify({
                propertyId: propertyIdFromUrl,
                sessionId:  existingSessionId,
                userAgent:  navigator.userAgent,
                language:   navigator.language,
              }),
            }).catch(() => { }); // silently ignore errors
            capturePostHogEvent('qr_code_scanned', {
              property_id: propertyIdFromUrl,
              browser_language: navigator.language
            });
          }

          // Validate hotel
          await validateHotel(propertyIdFromUrl);

          // Check if user already has a session
          const storedGuestName = localStorage.getItem(`${propertyIdFromUrl}GuestName`);

          if (storedGuestName) {
            setIsLoggedIn(true);
            setShowNameInterface(false);
            // Load conversation history if session exists
            const existingSessionId = localStorage.getItem(`${propertyIdFromUrl}SessionId`);
            if (existingSessionId) {
              identifyPostHogGuest(existingSessionId, {
                property_id: propertyIdFromUrl,
                session_source: 'url_property'
              });
              capturePostHogEvent('guest_session_restored', {
                property_id: propertyIdFromUrl,
                source: 'url_property'
              });
              loadHistory(existingSessionId, propertyIdFromUrl).then(data => handleHistoryResponse(data, null));
            }
          } else {
            setShowNameInterface(true);
          }
        } else {
          // No propertyId in URL, check localStorage
          const storedPropertyId = localStorage.getItem('propertyId');
          // Only check if storedPropertyId exists, token check happens inside
          if (storedPropertyId) {
            setPropertyId(storedPropertyId);
            await validateHotel(storedPropertyId);

            const storedGuestName = localStorage.getItem(`${storedPropertyId}GuestName`);

            if (storedGuestName) {
              setIsLoggedIn(true);
              setShowNameInterface(false);
              // Load conversation history if session exists
              const existingSessionId = localStorage.getItem(`${storedPropertyId}SessionId`);
              if (existingSessionId) {
                identifyPostHogGuest(existingSessionId, {
                  property_id: storedPropertyId,
                  session_source: 'stored_property'
                });
                capturePostHogEvent('guest_session_restored', {
                  property_id: storedPropertyId,
                  source: 'stored_property'
                });
                loadHistory(existingSessionId, storedPropertyId).then(data => handleHistoryResponse(data, null));
              }
            } else {
              setShowNameInterface(true);
            }
          } else {
            // No propertyId anywhere - show error
            setHotelValidationError('No property ID provided. Please use a valid link.');
            setIsValidatingHotel(false);
          }
        }
      } catch (error) {
        console.error('Error in hotel validation:', error);
        setHotelValidationError('Failed to validate hotel. Please try again.');
        setIsValidatingHotel(false);
      }
    };

    extractAndValidateHotel();
  }, []);

  const handleHistoryResponse = (historyData, hotelNameOverride) => {
    if (!historyData) return;
    
    const hName = hotelNameOverride || displayedHotelName || 'our';
    const welcome = `Hello, hola, γεια! Welcome to ${hName}! How can I assist you today?`;
    const welcomeMsg = { role: 'bot', content: welcome, html: marked(welcome), msgId: 'welcome-msg', isHistory: true };

    setMessages([
      ...(!historyData.hasMore ? [welcomeMsg] : []),
      ...historyData.messages
    ]);
    setOldestCursor(historyData.oldestCursor);
    setHasMoreHistory(historyData.hasMore);
  };

  // Load conversation history from backend
  const loadHistory = async (sid, pid, beforeCursor = null) => {
    const loadType = beforeCursor ? 'older' : 'initial';

    try {
      const url = new URL(`${process.env.REACT_APP_API_BASE}/chat/history`);
      url.searchParams.set('sessionId', sid);
      url.searchParams.set('hotelId', pid);
      if (beforeCursor) url.searchParams.set('before', beforeCursor);

      const res = await fetch(url.toString(), {
        headers: { 'x-webhook-secret': process.env.REACT_APP_WEBHOOK_SECRET }
      });

      if (!res.ok) {
        capturePostHogEvent('chat_history_load_failed', {
          property_id: pid,
          load_type: loadType,
          status: res.status
        });
        return null;
      }

      const data = await res.json();
      if (!data.messages) {
        capturePostHogEvent('chat_history_load_failed', {
          property_id: pid,
          load_type: loadType,
          reason: 'missing_messages'
        });
        return null;
      }

      // Convert messages to UI format
      const historyMessages = data.messages.map((msg, idx) => ({
        role: msg.role === 'bot' ? 'bot' : 'user',
        content: msg.content,
        html: msg.role === 'bot' ? marked(msg.content) : undefined,
        created_on: msg.created_on,
        messageId: msg.messageId,
        msgId: `${msg.role}-${msg.created_on}-${msg.messageId || idx}`,
        feedback: msg.feedback,
        images: msg.images,
        showImages: msg.showImages,
        isHistory: true
      }));

      capturePostHogEvent('chat_history_loaded', {
        property_id: pid,
        load_type: loadType,
        message_count: historyMessages.length,
        has_more: Boolean(data.hasMore)
      });

      return {
        messages: historyMessages,
        hasMore: data.hasMore,
        oldestCursor: historyMessages.length > 0 ? data.messages[0].created_on : null
      };
    } catch (e) {
      console.error('Error loading chat history:', e);
      capturePostHogEvent('chat_history_load_failed', {
        property_id: pid,
        load_type: loadType,
        reason: 'network_error'
      });
      return null;
    }
  };

  const loadMoreHistory = async () => {
    if (isLoadingHistory || !hasMoreHistory || !oldestCursor) return;
    setIsLoadingHistory(true);
    
    const currentPropertyId = localStorage.getItem('propertyId');
    const currentSessionId = currentPropertyId ? localStorage.getItem(`${currentPropertyId}SessionId`) : null;
    if (currentSessionId && currentPropertyId) {
      const historyData = await loadHistory(currentSessionId, currentPropertyId, oldestCursor);
      
      if (historyData) {
        // Build the welcome message if this is the end of history
        const welcome = `Hello, hola, γεια! Welcome to ${displayedHotelName || 'our'}! How can I assist you today?`;
        const welcomeMsg = { role: 'bot', content: welcome, html: marked(welcome) };

        setMessages(prev => [
          ...(!historyData.hasMore ? [welcomeMsg] : []),
          ...historyData.messages,
          ...prev
        ]);
        setOldestCursor(historyData.oldestCursor);
        setHasMoreHistory(historyData.hasMore);
      }
    }
    setIsLoadingHistory(false);
  };

  const checkTokenValidity = async () => {
    const token = localStorage.getItem('authToken');
    if (!token) {
      // If no token, we stay in current state based on GuestName already checked on mount
      return false;
    }

    try {
      const res = await fetch(`${process.env.REACT_APP_API_BASE}/auth/verify`, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });
      const data = await res.json();

      if (!res.ok || !data.valid) {
        console.log('Token invalid, removing token but preserving session identity...');
        localStorage.removeItem('authToken');

        // We do NOT remove GuestName or SessionId here anymore
        // This allows the user to stay "identified" even if the server session expired

        // Ensure state remains consistent with what's in storage
        const storedGuestName = localStorage.getItem(`${propertyId}GuestName`);
        setGuestName(storedGuestName || '');

        return false;
      }
      return true;
    } catch (error) {
      console.error('Token verification failed:', error);
      return true;
    }
  };

  useEffect(() => {
    if (propertyId) {
      checkTokenValidity();
    }
  }, [propertyId]);

  const sendFeedback = async (messageId, feedback) => {
    try {
      // Use the new backend endpoint instead of n8n
      const API_URL = `${process.env.REACT_APP_API_BASE}/messages/feedback`;
      const WEBHOOK_SECRET = process.env.REACT_APP_WEBHOOK_SECRET;

      await fetch(API_URL, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'x-webhook-secret': WEBHOOK_SECRET
        },
        body: JSON.stringify({ message_id: messageId, feedback }),
      });

      // Update local state so UI shows the feedback immediately
      setMessages(prev => prev.map(msg =>
        msg.messageId === messageId ? { ...msg, feedback } : msg
      ));
      capturePostHogEvent('message_feedback_submitted', {
        message_id: messageId,
        feedback: feedback === 1 ? 'positive' : 'negative'
      });
    } catch (error) {
      console.error('Failed to send feedback:', error);
    }
  };

  const removeEmojis = (text) => {
    return text.replace(/(\u00a9|\u00ae|[\u2000-\u3300]|\ud83c[\ud000-\udfff]|\ud83d[\ud000-\udfff]|\ud83e[\ud000-\udfff])/g, '');
  };

  const sendMessage = async (messageContent, source = 'typed') => {
    if (loading || !messageContent || !messageContent.trim()) return;

    const cleanMessageContent = removeEmojis(messageContent);
    const userMessageId = uuidv4();
    const userMessage = { role: 'user', content: cleanMessageContent, msgId: userMessageId };

    // Add user message and a placeholder for the bot message
    setMessages((prev) => [...prev, userMessage]);
    setLoading(true);
    capturePostHogEvent('chat_message_sent', {
      source,
      message_length: cleanMessageContent.length,
      has_session: Boolean(sessionId),
      has_guest_profile: Boolean(guestName && guestLastName)
    });

    let botMessageId = uuidv4();
    let accumulatedContent = '';
    let imagesCount = 0;
    let responseTracked = false;

    try {
      const token = localStorage.getItem('authToken');
      const response = await fetch(`${process.env.REACT_APP_API_BASE}/test-chat/stream`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': token ? `Bearer ${token}` : ''
        },
        body: JSON.stringify({
          message: cleanMessageContent,
          hotelId: propertyId,
          sessionId: sessionId || `session_${Date.now()}`,
          firstName: guestName,
          lastName: guestLastName
        }),
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const reader = response.body.getReader();
      const decoder = new TextDecoder();

      // Initialize the bot message in the state
      setMessages((prev) => [
        ...prev,
        { role: 'bot', content: '', html: '', messageId: botMessageId, msgId: botMessageId }
      ]);

      while (true) {
        const { value, done } = await reader.read();
        if (done) break;

        const chunk = decoder.decode(value, { stream: true });
        const lines = chunk.split('\n');

        for (const line of lines) {
          if (line.startsWith('data: ')) {
            const dataStr = line.slice(6).trim();
            if (dataStr === '[DONE]') continue;

            try {
              const data = JSON.parse(dataStr);
              if (data.type === 'token' && data.token) {
                accumulatedContent += data.token;

                // Update the last message (the bot message) with the new token
                setMessages((prev) => {
                  const newMessages = [...prev];
                  const lastIdx = newMessages.length - 1;
                  if (lastIdx >= 0 && newMessages[lastIdx].role === 'bot') {
                    newMessages[lastIdx] = {
                      ...newMessages[lastIdx],
                      content: accumulatedContent,
                      html: marked(accumulatedContent),
                      messageId: data.message_id || botMessageId
                    };
                  }
                  return newMessages;
                });
              } else if (data.type === 'images' && data.images && data.images.length > 0) {
                imagesCount = data.images.length;
                // Preload images in the background (they arrive before tokens)
                data.images.forEach((url) => {
                  const img = new Image();
                  img.src = url;
                });
                // Attach images to the bot message for rendering after stream completes
                setMessages((prev) => {
                  const newMessages = [...prev];
                  const lastIdx = newMessages.length - 1;
                  if (lastIdx >= 0 && newMessages[lastIdx].role === 'bot') {
                    newMessages[lastIdx] = {
                      ...newMessages[lastIdx],
                      images: data.images
                    };
                  }
                  return newMessages;
                });
              } else if (data.type === 'done') {
                if (!responseTracked) {
                  responseTracked = true;
                  capturePostHogEvent('chat_response_received', {
                    source,
                    response_length: accumulatedContent.length,
                    message_id: data.message_id || botMessageId,
                    images_count: imagesCount,
                    completion_source: 'done_event'
                  });
                }
                // Unlock UI early when agent signals it's done
                setLoading(false);
                // Mark the bot message to reveal preloaded images
                setMessages((prev) => {
                  const newMessages = [...prev];
                  const lastIdx = newMessages.length - 1;
                  if (lastIdx >= 0 && newMessages[lastIdx].role === 'bot') {
                    newMessages[lastIdx] = {
                      ...newMessages[lastIdx],
                      showImages: true
                    };
                  }
                  return newMessages;
                });
              } else if (data.type === 'error') {
                throw new Error(data.error || 'Unknown agent error');
              }
            } catch (e) {
              console.error('Error parsing SSE chunk:', e, dataStr);
              // If it's not valid JSON, it might be a partial chunk or something else
            }
          }
        }
      }

      if (!responseTracked && accumulatedContent) {
        responseTracked = true;
        capturePostHogEvent('chat_response_received', {
          source,
          response_length: accumulatedContent.length,
          message_id: botMessageId,
          images_count: imagesCount,
          completion_source: 'stream_end'
        });
      }
    } catch (e) {
      console.error('Stream error:', e);
      capturePostHogEvent('chat_response_failed', {
        source,
        error_message: e.message ? e.message.slice(0, 120) : 'unknown'
      });
      const errorMsg = 'Sorry, there was an error processing your request.';
      setMessages((prev) => {
        // Find if we already added a bot message and replace it, or add a new one
        const newMessages = [...prev];
        const lastIdx = newMessages.length - 1;
        if (lastIdx >= 0 && newMessages[lastIdx].role === 'bot' && newMessages[lastIdx].content === '') {
          newMessages[lastIdx] = { role: 'bot', content: errorMsg, html: marked(errorMsg), messageId: uuidv4() };
          return newMessages;
        }
        return [...prev, { role: 'bot', content: errorMsg, html: marked(errorMsg), messageId: uuidv4() }];
      });
    } finally {
      setLoading(false);
    }
  };

  const handleStartChatFromWelcome = (initialMessage) => {
    capturePostHogEvent('welcome_start_chat_clicked', {
      has_initial_message: Boolean(initialMessage && initialMessage.trim())
    });
    setHasSeenWelcome(true);
    if (initialMessage && initialMessage.trim()) {
      sendMessage(initialMessage, 'welcome');
    }
  };

  const handleWelcomeContinue = () => {
    capturePostHogEvent('welcome_continue_clicked');
    setHasSeenWelcome(true);
  };

  const [guestName, setGuestName] = useState('');
  const [guestLastName, setGuestLastName] = useState('');

  useEffect(() => {
    const storedGuestName = localStorage.getItem(`${propertyId}GuestName`);
    const storedGuestLastName = localStorage.getItem(`${propertyId}GuestLastName`);
    if (storedGuestName) {
      setGuestName(storedGuestName);
    }
    if (storedGuestLastName) {
      setGuestLastName(storedGuestLastName);
    }
  }, [propertyId]);

  const handleNameSubmit = async (name, lastName) => {
    try {
      // Generate unique fingerprint using FingerprintJS
      const fp = await FingerprintJS.load();
      const result = await fp.get();
      const fingerprint = result.visitorId;

      console.log('Generated fingerprint:', fingerprint);

      // Store fingerprint as session ID
      const currentSessionKey = `${propertyId}SessionId`;
      localStorage.setItem(currentSessionKey, fingerprint);
      localStorage.setItem(`${propertyId}GuestName`, name.trim());
      localStorage.setItem(`${propertyId}GuestLastName`, lastName.trim());

      // Generate a simple auth token (you may want to get this from backend)
      const authToken = uuidv4();
      localStorage.setItem('authToken', authToken);

      identifyPostHogGuest(fingerprint, {
        session_source: 'fingerprint'
      });
      capturePostHogEvent('guest_session_started', {
        session_source: 'fingerprint'
      });

      setGuestName(name.trim());
      setGuestLastName(lastName.trim());
      setShowNameInterface(false);
      setIsLoggedIn(true);
    } catch (error) {
      console.error('Error generating fingerprint:', error);
      // Fallback to UUID if FingerprintJS fails
      const newId = uuidv4();
      const currentSessionKey = `${propertyId}SessionId`;
      localStorage.setItem(currentSessionKey, newId);
      localStorage.setItem(`${propertyId}GuestName`, name.trim());
      localStorage.setItem(`${propertyId}GuestLastName`, lastName.trim());

      const authToken = uuidv4();
      localStorage.setItem('authToken', authToken);

      identifyPostHogGuest(newId, {
        session_source: 'uuid_fallback'
      });
      capturePostHogEvent('guest_session_started', {
        session_source: 'uuid_fallback'
      });

      setGuestName(name.trim());
      setGuestLastName(lastName.trim());
      setShowNameInterface(false);
      setIsLoggedIn(true);
    }
  };

  const handleLogout = () => {
    const currentPropertyId = localStorage.getItem('propertyId');
    capturePostHogEvent('guest_logged_out', {
      property_id: currentPropertyId || undefined
    });

    // Only remove guest name and auth token
    // Keep sessionId (fingerprint) and propertyId for continuity
    localStorage.removeItem('authToken');
    if (currentPropertyId) {
      localStorage.removeItem(`${currentPropertyId}GuestName`);
      localStorage.removeItem(`${currentPropertyId}GuestLastName`);
    }

    // Reset state to show name interface
    setIsLoggedIn(false);
    setHasSeenWelcome(false);
    setGuestName('');
    setGuestLastName('');
    setShowNameInterface(true);
  };

  // Show loading or error state during hotel validation
  if (isValidatingHotel) {
    return (
      <div className="h-[100dvh] w-full flex items-center justify-center bg-neutral-900">
        <div className="text-white text-center">
          <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-white mb-4"></div>
          <p>Validating hotel...</p>
        </div>
      </div>
    );
  }

  if (hotelValidationError) {
    return (
      <div className="h-[100dvh] w-full flex items-center justify-center bg-neutral-900">
        <div className="text-white text-center max-w-md px-4">
          <h1 className="text-2xl font-semibold mb-4">Oops!</h1>
          <p className="text-white/70 mb-6">{hotelValidationError}</p>
          <button
            onClick={() => window.location.reload()}
            className="px-6 py-3 bg-white text-black rounded-2xl font-medium hover:bg-white/90 transition-colors"
          >
            Try Again
          </button>
        </div>
      </div>
    );
  }

  if (!isLoggedIn) {
    if (showNameInterface) {
      return (
        <div key="name-interface">
          <GuestNameInterface
            hotelLogoPath={hotelLogoPath}
            displayedHotelName={displayedHotelName}
            onNameSubmit={handleNameSubmit}
          />
        </div>
      );
    }
  }

  if (!hasSeenWelcome) {
    return (
      <div key="welcome-page" className="animate-fade-in-up fixed inset-0 w-full h-full overflow-hidden bg-[#f5f5f7]">
        <WelcomePage
          hotelName={displayedHotelName}
          guestName={guestName}
          onContinue={handleWelcomeContinue}
          onStartChat={handleStartChatFromWelcome}
          onLogout={handleLogout}
        />
      </div>
    );
  }

  return (
    <div key="chat-interface" className="animate-fade-in-up fixed inset-0 w-full h-full bg-gray-900 pointer-events-auto z-0">
      <ChatInterface
        messages={messages}
        loading={loading}
        onSendMessage={sendMessage}
        onSendFeedback={sendFeedback}
        hotelLogoPath={hotelLogoPath}
        displayedHotelName={displayedHotelName}
        suggestions={suggestions}
        onBack={() => setHasSeenWelcome(false)}
        guestName={guestName}
        onLoadMore={loadMoreHistory}
        hasMoreHistory={hasMoreHistory}
        isLoadingHistory={isLoadingHistory}
        onVoiceInteraction={capturePostHogEvent}
      />
    </div>
  );
}
